import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { TenantProfileDocumentStatus } from '@libs/constants';
import { type SingularKeys, useTrans } from '@libs/i18n';
import { useDebounce } from '@uidotdev/usehooks';

import {
  useBulkExtractDocuments,
  useDeleteProfileDocument,
  useDocumentStatusSummary,
  useListProfileDocumentsPaginated,
  useRetryClassifyDocuments,
} from '../api/tenant-profile-documents-api';
import { useTemplateOutline } from '../api/tenant-profile-template-api';
import { DocumentFilters } from '../components/hub/DocumentFilters';
import { DocumentHubEmptyState } from '../components/hub/DocumentHubEmptyState';
import { DocumentHubHeader } from '../components/hub/DocumentHubHeader';
import { DocumentsTable } from '../components/hub/DocumentsTable';
import { type HubDisplayStatus } from '../components/hub/documentStatusMeta';
import {
  DocumentStatusStrip,
  type StatusCounts,
} from '../components/hub/DocumentStatusStrip';
import { GlobalUploadBar } from '../organization-profile/components/GlobalUploadBar';
import { ORG_TOKENS } from '../organization-profile/utils/tokens';

const ZERO_COUNTS: StatusCounts = {
  classifying: 0,
  ready_to_extract: 0,
  extracting: 0,
  pending_review: 0,
  done: 0,
  failed: 0,
  irrelevant: 0,
};

const ALL_STATUSES = Object.keys(ZERO_COUNTS) as HubDisplayStatus[];

function isHubStatus(value: string | null): value is HubDisplayStatus {
  return value != null && (ALL_STATUSES as string[]).includes(value);
}

/**
 * Document Hub — the centralized destination for uploading, tracking, filtering
 * and managing every document on the organization profile. Reuses the existing
 * tenant-profile APIs (which already poll while work is in flight); all counts
 * and filter buckets are derived client-side from the documents list.
 *
 * Supports `?status=<displayStatus>` and `?category=<key>` for deep-links and
 * the per-category breadcrumb.
 */
export const TenantProfileHubPage: FC = observer(() => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');

  const { data: template } = useTemplateOutline();
  const { data: summary } = useDocumentStatusSummary();
  const deleteDoc = useDeleteProfileDocument();
  const retryClassify = useRetryClassifyDocuments();
  const bulkExtract = useBulkExtractDocuments();

  const activeStatus = isHubStatus(searchParams.get('status'))
    ? (searchParams.get('status') as HubDisplayStatus)
    : null;
  const activeCategory = searchParams.get('category');

  // ── Server-driven table (pagination + filters on the backend) ─────────────
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(search, 400);

  // The hub tab "irrelevant" maps to the backend `invalid` status; every other
  // display status is identical to its backend value.
  const statusForApi: TenantProfileDocumentStatus | null =
    activeStatus === 'irrelevant'
      ? TenantProfileDocumentStatus.INVALID
      : (activeStatus as TenantProfileDocumentStatus | null);

  const {
    data: paged,
    isLoading,
    isFetching,
    error,
  } = useListProfileDocumentsPaginated({
    page,
    limit: 10,
    searchBy: debouncedSearch.trim() || undefined,
    categoryKey: activeCategory,
    status: statusForApi,
  });

  const rows = paged?.rows ?? [];
  const meta = paged?.meta;
  const total = summary?.total ?? 0;

  // Reset to the first page whenever the active filters/search change.
  useEffect(() => {
    setPage(1);
  }, [activeStatus, activeCategory, debouncedSearch]);

  const categoryOptions = useMemo(
    () =>
      (template?.categories ?? []).map((c) => ({
        key: c.key,
        name: c.metadata.name || c.key,
      })),
    [template],
  );

  const categoryName = useMemo(() => {
    const map = new Map(categoryOptions.map((c) => [c.key, c.name]));
    return (key: string | null) => (key ? map.get(key) ?? key : null);
  }, [categoryOptions]);

  // Status-strip / filter counts come from the backend summary (so the hub no
  // longer loads every document just to tally them). Map the backend `invalid`
  // bucket onto the hub's `irrelevant` display status.
  const counts = useMemo<StatusCounts>(() => {
    const by = summary?.byStatus;
    return {
      classifying: by?.[TenantProfileDocumentStatus.CLASSIFYING] ?? 0,
      ready_to_extract: by?.[TenantProfileDocumentStatus.READY_TO_EXTRACT] ?? 0,
      extracting: by?.[TenantProfileDocumentStatus.EXTRACTING] ?? 0,
      pending_review: by?.[TenantProfileDocumentStatus.PENDING_REVIEW] ?? 0,
      done: by?.[TenantProfileDocumentStatus.DONE] ?? 0,
      failed: by?.[TenantProfileDocumentStatus.FAILED] ?? 0,
      irrelevant: by?.[TenantProfileDocumentStatus.INVALID] ?? 0,
    };
  }, [summary]);

  const setParam = (key: 'status' | 'category', value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const goToReview = (id: string) =>
    navigate(`/tenant-profile/document-hub/${id}/review`);

  const actionCallbacks = (successKey: SingularKeys) => ({
    onSuccess: () => enqueueSnackbar(t(successKey), { variant: 'success' }),
    onError: () =>
      enqueueSnackbar(t('app.documentHub.toast.actionFailed'), {
        variant: 'error',
      }),
  });

  const onExtract = (id: string) =>
    bulkExtract.mutate([id], actionCallbacks('app.documentHub.toast.extractStarted'));
  const onRetryClassify = (id: string) =>
    retryClassify.mutate([id], actionCallbacks('app.documentHub.toast.reRouted'));
  const onDelete = (id: string) =>
    deleteDoc.mutate(id, actionCallbacks('app.documentHub.toast.deleted'));

  const hasAnyFilter = Boolean(
    activeStatus || activeCategory || debouncedSearch.trim(),
  );

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        backgroundColor: 'background.default',
        borderRadius: `${ORG_TOKENS.radiusLg}px`,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Box
        sx={{
          width: '100%',
          px: { xs: 2, md: 4 },
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        <DocumentHubHeader
          fileCount={total}
          categoryLabel={categoryName(activeCategory)}
        />

        <GlobalUploadBar />

        <DocumentStatusStrip
          counts={counts}
          onReviewAll={() => setParam('status', 'pending_review')}
        />

        <DocumentFilters
          total={total}
          counts={counts}
          activeStatus={activeStatus}
          onStatusChange={(status) => setParam('status', status)}
          categories={categoryOptions}
          activeCategory={activeCategory}
          onCategoryChange={(key) => setParam('category', key)}
          search={search}
          onSearchChange={setSearch}
        />

        {error ? (
          <CenteredMessage tone="error">
            {t('app.documentHub.loadError')}
          </CenteredMessage>
        ) : isLoading ? (
          <CenteredMessage>{t('app.documentHub.review.loading')}</CenteredMessage>
        ) : rows.length === 0 ? (
          <DocumentHubEmptyState filtered={total > 0 && hasAnyFilter} />
        ) : (
          <DocumentsTable
            docs={rows}
            categoryName={categoryName}
            onReview={goToReview}
            onExtract={onExtract}
            onRetryClassify={onRetryClassify}
            onDelete={onDelete}
            page={meta?.currentPage ?? page}
            totalPages={meta?.totalPages ?? 1}
            totalCount={meta?.totalCount ?? rows.length}
            onPageChange={setPage}
            isLoading={isFetching}
          />
        )}
      </Box>
    </Box>
  );
});

const CenteredMessage: FC<{ children: React.ReactNode; tone?: 'error' }> = observer(
  ({ children, tone }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <Typography
        variant="textSM_regular"
        color={tone === 'error' ? 'yellow.800' : 'text.tertiary'}
      >
        {children}
      </Typography>
    </Box>
  ),
);
